import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { Item } from "./item";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface LocationSlice {
  items: Item[];
  formData: Partial<Item>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: LocationSlice = {
  items: [],
  formData: { itemName: "", status: 1 },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchItems = createAsyncThunk(
  "item/fetchItems",
  async (params: { page?: number; limit?: number; search?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      const url = `/master/item${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await enhancedApiClient.get(url);
      if (isSuccessResponse(response)) {
        console.log("Fetch item response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch items");
      }
    } catch (error: any) {
      console.error("Fetch items error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch items";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create State
export const createItem = createAsyncThunk(
  "item/createItem",
  async (data: Partial<Item>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/item", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create item");
      }
    } catch (error: any) {
      console.error("Create item error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create color";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit item
export const editItem = createAsyncThunk(
  "item/editItem",
  async ({ id, data }: { id: number; data: Partial<Item> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/item/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update item");
      }
    } catch (error: any) {
      console.error("Edit item error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update item";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle item status
export const toggleItemStatus = createAsyncThunk(
  "item/toggleItemStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/item/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle item status");
      }
    } catch (error: any) {
      console.error("Toggle item status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle item status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    setItemName: (state, action: PayloadAction<string>) => {
      state.formData.itemName = action.payload;
    },
    setItemStatus: (state, action: PayloadAction<number>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { itemName: "", status: 1 };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.records;
        state.pagination = action.payload.pagination;
        console.log("items fetched successfully:", action.payload);
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if(action.payload) {
          const newLocation: Item = {
            id: action.payload.itemId,
            itemId: action.payload.itemId,
            itemName: action.payload.itemName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action:action.payload.action,
          };
          state.items.unshift(newLocation);
          }
        state.formData = { itemName: "", status: 1 };
      })
      .addCase(createItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editItem.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedLocation : Item = {
          id: action.payload.itemId,
          itemId: action.payload.itemId,
          itemName: action.payload.itemName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action:action.payload.action,
        }
        const index = state.items.findIndex(s => s.id === updatedLocation.id);
        if (index !== -1) {
          state.items[index] = updatedLocation;
        }
        state.formData = { itemName: "", status: 1 };
      })
      .addCase(editItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
      .addCase(toggleItemStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleItemStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedLocation : Item = {
          id: action.payload.itemId,
          itemId: action.payload.itemId,
          itemName: action.payload.itemName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.items.findIndex(s => s.itemId === updatedLocation.itemId);
        if (index !== -1) {
          state.items = [
            ...state.items.slice(0, index),
            updatedLocation,
            ...state.items.slice(index + 1)
          ];
        }
      })
      .addCase(toggleItemStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setItemName, setItemStatus, resetForm } = itemSlice.actions;
// ✅ Selectors
export const SelectedItems = (state: RootState) => state.item.items;
export const SelectedItemLoading = (state: RootState) => state.item.loading;
export const selectItemError = (state: RootState) => state.item.error;
export const selectItemPagination = (state: RootState) => state.item.pagination

export default itemSlice.reducer;
